import { Search, ShieldCheck, UserRoundCog } from "lucide-react";
import { useEffect, useState } from "react";
import { useReverification } from "@tribunalea/auth/react";

function formatLastActive(value) {
  if (!value) return "Sin actividad registrada";
  return `Última actividad: ${new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value))}`;
}

export function SupportImpersonationPage({ api, access }) {
  const startVerifiedImpersonation = useReverification(api.startImpersonation);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingUserId, setOpeningUserId] = useState(null);
  const [loadingWorkspaceUserId, setLoadingWorkspaceUserId] = useState(null);
  const [workspaceSelection, setWorkspaceSelection] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!access?.enabled) return undefined;
    let active = true;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const result = await api.searchImpersonationUsers(query);
        if (active) setUsers(result);
      } catch (searchError) {
        if (active) {
          setUsers([]);
          setError(searchError.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [access?.enabled, api, query]);

  const selectUser = async (user) => {
    if (workspaceSelection?.user.id === user.id) {
      setWorkspaceSelection(null);
      return;
    }
    setLoadingWorkspaceUserId(user.id);
    setError("");
    try {
      setWorkspaceSelection(await api.impersonationWorkspaces(user.id));
    } catch (workspaceError) {
      setError(workspaceError.message);
    } finally {
      setLoadingWorkspaceUserId(null);
    }
  };

  const openAsUser = async (user, workspace) => {
    const reason = window.prompt(
      "Indicá brevemente el motivo del acceso para dejarlo en la auditoría:",
      "Diagnóstico o asistencia de configuración",
    )?.trim();
    if (!reason) return;
    if (reason.length < 5) {
      setError("El motivo debe tener al menos 5 caracteres.");
      return;
    }
    const confirmed = window.confirm(
      `Vas a abrir ${workspace.label} como ${user.fullName}. `
      + "Tu sesión de desarrollador seguirá activa y todas las acciones quedarán auditadas. ¿Continuar?",
    );
    if (!confirmed) return;

    setOpeningUserId(user.id);
    setError("");
    try {
      await startVerifiedImpersonation({
        userId: user.id,
        workspaceId: workspace.id,
        reason,
      });
      window.location.assign(
        import.meta.env.VITE_PRAXIS_URL || (
          import.meta.env.DEV
            ? "http://localhost:5173"
            : "https://praxis.tribunalea.com"
        ),
      );
    } catch (openError) {
      if (openError?.code !== "reverification_cancelled") {
        setError(openError.message);
      }
      setOpeningUserId(null);
    }
  };

  return (
    <div className="support-access-page">
      <header className="support-access-heading">
        <span className="support-access-eyebrow">CUENTA · DESARROLLO</span>
        <h1>Acceso de soporte</h1>
        <p>
          Abrí Tribunalea exactamente como lo ve un usuario para reproducir un
          problema o ayudarlo con su configuración.
        </p>
      </header>

      <section className="support-access-notice">
        <ShieldCheck size={20} aria-hidden="true" />
        <span>
          <strong>Acceso controlado y auditable</strong>
          El usuario no recibe tu contraseña ni vos la suya. El modo soporte
          queda identificado durante toda la sesión.
        </span>
      </section>

      {access?.requiresReverification ? (
        <div className="support-access-notice" role="status">
          <ShieldCheck size={20} aria-hidden="true" />
          <span>
            <strong>Verificación pendiente</strong>
            Cuando abras una cuenta, Clerk te pedirá confirmar tu identidad y
            luego continuará automáticamente.
          </span>
        </div>
      ) : null}

      {error ? <div className="support-access-error" role="alert">{error}</div> : null}

      <section className="support-access-card">
        <label className="support-access-search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por nombre, email o ID de Clerk"
            autoComplete="off"
          />
        </label>

        <div className="support-access-results" aria-live="polite">
          {loading ? (
            <div className="support-access-state">Buscando usuarios…</div>
          ) : users.length ? users.map((user) => (
            <article className="support-user-row" key={user.id}>
              {user.imageUrl ? (
                <img src={user.imageUrl} alt="" />
              ) : (
                <span className="support-user-avatar" aria-hidden="true">
                  {user.fullName.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="support-user-copy">
                <strong>{user.fullName}</strong>
                <span>{user.email || user.id}</span>
                <small>{formatLastActive(user.lastActiveAt)}</small>
              </span>
              <button
                type="button"
                className="support-user-open"
                disabled={
                  user.banned
                  || user.locked
                  || Boolean(openingUserId)
                  || Boolean(loadingWorkspaceUserId)
                }
                title={user.banned || user.locked ? "Cuenta bloqueada" : undefined}
                onClick={() => selectUser(user)}
              >
                <UserRoundCog size={17} aria-hidden="true" />
                {loadingWorkspaceUserId === user.id
                  ? "Buscando espacios…"
                  : workspaceSelection?.user.id === user.id
                    ? "Cerrar espacios"
                    : "Elegir espacio"}
              </button>
              {workspaceSelection?.user.id === user.id ? (
                <div className="support-workspace-list">
                  <span>¿Qué espacio querés abrir?</span>
                  {workspaceSelection.workspaces.map((workspace) => (
                    <button
                      type="button"
                      key={workspace.id}
                      disabled={Boolean(openingUserId)}
                      onClick={() => openAsUser(user, workspace)}
                    >
                      <strong>{workspace.label}</strong>
                      <small>{workspace.role}</small>
                    </button>
                  ))}
                </div>
              ) : null}
            </article>
          )) : (
            <div className="support-access-state">
              No encontramos usuarios con esa búsqueda.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
